import { useState } from 'react';
import {CURATED_CATEGORIES,ALL_CATEGORIES} from '../utils/layers.js';
import { CATEGORY_COLORS } from '../utils/visuals.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import BottomSheet from './BottomSheet.jsx';

const GROUPS = [
  { label: 'Orbital',  cats: ['station','starlink','oneweb','kuiper','active','meo','geo'] },
  { label: 'Science',  cats: ['weather','gps','science','amateur'] },
  { label: 'Alerts',   cats: ['reentry','conjunction','debris'] },
  { label: 'Surface',  cats: ['groundstation','launches'] },
  { label: 'Atmo',     cats: ['aurora','goesimg'] },
];

const LABELS = {
  station:'Stations', starlink:'Starlink', oneweb:'OneWeb', kuiper:'Kuiper',
  weather:'Weather', gps:'GNSS', science:'Science', amateur:'Amateur',
  active:'Payloads', meo:'MEO', geo:'GEO', debris:'Debris',
  reentry:'⚠ Reentry', conjunction:'⚡ Conjunctions', groundstation:'Dishes',
  aviation:'✈ Aviation', aurora:'🌌 Aurora', launches:'🚀 Launches', goesimg:'🌩 Weather Img',
};

const ALL_CATS = GROUPS.flatMap(g => g.cats);
export const DEFAULT_CATEGORIES = CURATED_CATEGORIES;

const STEPS = [0, 0.5, 1, 2, 3, 6, 12];
function formatOffset(sec) {
  if (sec === 0) return 'Now';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h === 0) return `+${m}m`;
  if (m === 0) return `+${h}h`;
  return `+${h}h ${m}m`;
}

function chip(active, rawColor) {
  const color = rawColor.length > 7 ? rawColor.substring(0, 7) : rawColor;
  return {
    padding: '5px 12px', borderRadius: 99, fontSize: 11, fontWeight: 600,
    fontFamily: 'var(--mono)', cursor: 'pointer', flexShrink: 0,
    border: `1px solid ${active ? color : 'var(--hair)'}`,
    background: active ? color + '2a' : 'transparent',
    color: active ? color : 'var(--txt-dim)',
    transition: 'all 0.12s', userSelect: 'none', whiteSpace: 'nowrap',
  };
}

export default function FilterTray({ filters, onChange }) {
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);

  const toggle = (cat) => {
    const next = filters.categories.includes(cat)
      ? filters.categories.filter(c => c !== cat)
      : [...filters.categories, cat];
    onChange({ ...filters, categories: next });
  };

  const allOn  = () => onChange({ ...filters, categories: ALL_CATEGORIES });
  const allOff = () => onChange({ ...filters, categories: [] });

  const offsetSec = filters.time_offset_sec || 0;
  const maxSec    = 12 * 3600;
  const isOffset  = offsetSec > 0;
  const activeCount = filters.categories.length;

  if (isMobile) {
    return (
      <>
        {/* Mobile bottom bar, time control + layer button */}
        <div style={{
          width: '100%',
          background: 'linear-gradient(to top, rgba(10,10,12,0.96) 70%, transparent)',
          padding: '8px 12px 16px',
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          {/* Time scrubber row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
            <span style={{ fontSize: 9, color: 'var(--txt-dim)', fontFamily: 'var(--mono)', letterSpacing: 1 }}>TIME</span>
            <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'var(--mono)',
                           color: isOffset ? 'var(--accent)' : 'var(--txt-dim)', minWidth: 40, textAlign: 'center' }}>
              {formatOffset(offsetSec)}
            </span>
            <input
              type="range" min={0} max={maxSec} step={300} value={offsetSec}
              onChange={e => onChange({ ...filters, time_offset_sec: parseInt(e.target.value) })}
              style={{ flex: 1, accentColor: 'var(--accent)', cursor: 'pointer' }}
            />
            {isOffset && (
              <button
                onClick={() => onChange({ ...filters, time_offset_sec: 0 })}
                style={{ padding: '3px 8px', borderRadius: 99, fontSize: 10,
                         fontFamily: 'var(--mono)', cursor: 'pointer', border: 'none',
                         background: 'var(--amber-2)', color: 'var(--accent)', fontWeight: 700 }}
              >Now</button>
            )}
          </div>

          {/* Layer toggle button */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={() => setSheetOpen(true)}
              style={{
                padding: '8px 20px', borderRadius: 99, fontSize: 12, fontWeight: 700,
                fontFamily: 'var(--mono)', cursor: 'pointer',
                border: '1px solid var(--hair-2)',
                background: 'var(--bg-2)',
                color: 'var(--txt-dim)', letterSpacing: 0.5,
              }}
            >
              LAYERS · {activeCount} active
            </button>
          </div>
        </div>

        {/* Bottom sheet with chip grid */}
        <BottomSheet open={sheetOpen} onClose={() => setSheetOpen(false)} title="LAYERS" maxHeight="70vh">
          {GROUPS.map(group => (
            <div key={group.label} style={{ padding: '6px 16px 10px' }}>
              <div style={{ fontSize: 9, color: 'var(--txt-dim)', fontFamily: 'var(--mono)',
                            letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
                {group.label}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {group.cats.map(cat => (
                  <button
                    key={cat}
                    style={chip(filters.categories.includes(cat), CATEGORY_COLORS[cat] || '#94A3B8')}
                    onClick={() => toggle(cat)}
                  >
                    {LABELS[cat]}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, padding: '8px 16px 0',
                        borderTop: '1px solid var(--hair)', marginTop: 4 }}>
            <button onClick={allOn}
              style={{ flex: 1, padding: '10px', borderRadius: 8, fontSize: 11,
                       fontFamily: 'var(--mono)', cursor: 'pointer', fontWeight: 700,
                       border: '1px solid var(--hair-2)', background: 'transparent', color: 'var(--txt-dim)' }}>
              ALL ON
            </button>
            <button onClick={allOff}
              style={{ flex: 1, padding: '10px', borderRadius: 8, fontSize: 11,
                       fontFamily: 'var(--mono)', cursor: 'pointer', fontWeight: 700,
                       border: '1px solid var(--hair-2)', background: 'transparent', color: 'var(--txt-dim)' }}>
              ALL OFF
            </button>
          </div>
        </BottomSheet>
      </>
    );
  }

  // ── Desktop layout (unchanged) ───────────────────────────────────────────────
  return (
    <div style={{
      width: '100%',
      background: 'linear-gradient(to top, rgba(10,10,12,0.95) 60%, rgba(10,10,12,0))',
      padding: '8px 16px 12px',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
        <span style={{ fontSize: 9, color: 'var(--txt-mute)', letterSpacing: 1, fontFamily: 'var(--mono)' }}>TIME</span>
        <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'var(--mono)',
                       minWidth: 40, textAlign: 'center',
                       color: isOffset ? 'var(--accent)' : 'var(--txt-dim)', transition: 'color 0.2s' }}>
          {formatOffset(offsetSec)}
        </span>
        <input
          type="range" min={0} max={maxSec} step={300} value={offsetSec}
          onChange={e => onChange({ ...filters, time_offset_sec: parseInt(e.target.value) })}
          style={{ width: 180, accentColor: 'var(--accent)', cursor: 'pointer' }}
        />
        <div style={{ display: 'flex', gap: 2 }}>
          {STEPS.map(h => (
            <button key={h}
              style={{
                padding: '2px 7px', borderRadius: 99, fontSize: 10,
                fontFamily: 'var(--mono)', cursor: 'pointer', border: 'none',
                background: offsetSec === h * 3600 ? 'var(--amber-2)' : 'transparent',
                color: offsetSec === h * 3600 ? 'var(--accent)' : 'var(--txt-dim)',
                fontWeight: offsetSec === h * 3600 ? 700 : 400,
              }}
              onClick={() => onChange({ ...filters, time_offset_sec: h * 3600 })}
            >
              {h === 0 ? 'Now' : `+${h}h`}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 4,
                    flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: 1 }}>
        <span style={{ fontSize: 9, color: 'var(--txt-dim)', fontFamily: 'var(--mono)',
                       textTransform: 'uppercase', letterSpacing: 0.8, flexShrink: 0 }}>
          Layers
        </span>
        {GROUPS.map((group, gi) => (
          <span key={group.label} style={{ display: 'contents' }}>
            {gi > 0 && <span style={{ width: 1, height: 16, background: 'var(--hair-2)',
                                      flexShrink: 0, margin: '0 4px', display: 'inline-block' }} />}
            {group.cats.map(cat => (
              <button key={cat}
                style={chip(filters.categories.includes(cat), CATEGORY_COLORS[cat] || '#94A3B8')}
                onClick={() => toggle(cat)} title={cat}
              >
                {LABELS[cat]}
              </button>
            ))}
          </span>
        ))}
        <span style={{ width: 1, height: 16, background: 'var(--hair-2)',
                       flexShrink: 0, margin: '0 4px', display: 'inline-block' }} />
        <button onClick={allOn}
          style={{ padding: '3px 9px', borderRadius: 99, fontSize: 10, fontWeight: 600,
                   fontFamily: 'var(--mono)', cursor: 'pointer', flexShrink: 0,
                   border: '1px solid var(--hair)', background: 'transparent', color: 'var(--txt-dim)' }}>
          All
        </button>
        <button onClick={allOff}
          style={{ padding: '3px 9px', borderRadius: 99, fontSize: 10, fontWeight: 600,
                   fontFamily: 'var(--mono)', cursor: 'pointer', flexShrink: 0,
                   border: '1px solid var(--hair)', background: 'transparent', color: 'var(--txt-dim)' }}>
          None
        </button>
      </div>
    </div>
  );
}
